import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import jwtDecode from 'jwt-decode';

// Create context
const AuthContext = createContext();

// API URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [postLoginLoading, setPostLoginLoading] = useState(false);

  // Check if token is valid
  const isTokenValid = (token) => {
    if (!token) return false;
    
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 > Date.now();
    } catch (error) {
      return false;
    }
  };

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (storedToken && isTokenValid(storedToken)) {
        setToken(storedToken);
        
        try {
          // Set default headers for all requests
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          
          // Fetch user data
          const response = await axios.get(`${API_URL}/users/me`);
          setUser(response.data.data.user);
        } catch (error) {
          console.error('Error fetching user data:', error);
          logout();
        }
      } else {
        // Clear invalid token
        if (storedToken) {
          localStorage.removeItem('token');
        }
        setToken(null);
        setUser(null);
      }
      
      setLoading(false);
    };
    
    initAuth();
  }, []);

  // Register user
  const register = async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API_URL}/auth/register`, userData);
      const { token, user } = response.data.data;
      
      // Store token and set user
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setToken(token);
      setUser(user);

      setPostLoginLoading(true);
      
      return { success: true };
    } catch (error) {
      setError(error.response?.data?.message || 'Registration failed');
      return { success: false, error: error.response?.data?.message || 'Registration failed' };
    } finally {
      setLoading(false);
    }
  };

  /** Set Envoy JWT session (login, magic-link bridge). */
  const establishAppSession = ({ token, user }) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setToken(token);
    setUser(user);
    sessionStorage.setItem('envoy:justLoggedIn', '1');
  };

  // Login user
  const login = async (credentials) => {
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/auth/login`, credentials);
      const { token, user } = response.data.data;
      establishAppSession({ token, user });

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      setError(message);
      return {
        success: false,
        error: message,
        code: error.response?.data?.code,
        ersUrl: error.response?.data?.ersUrl || null,
      };
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setPostLoginLoading(false);
  };

  // Forgot password
  const forgotPassword = async (email) => {
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      return { success: true };
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send reset email');
      return { success: false, error: error.response?.data?.message || 'Failed to send reset email' };
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (token, password) => {
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API_URL}/auth/reset-password`, { token, password });
      return { success: true };
    } catch (error) {
      setError(error.response?.data?.message || 'Password reset failed');
      return { success: false, error: error.response?.data?.message || 'Password reset failed' };
    } finally {
      setLoading(false);
    }
  };

  // Update user profile
  const updateProfile = async (userId, userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.put(`${API_URL}/users/${userId}`, userData);
      setUser(response.data.data.user);
      return { success: true, user: response.data.data.user };
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update profile');
      return { success: false, error: error.response?.data?.message || 'Failed to update profile' };
    } finally {
      setLoading(false);
    }
  };

    const uploadAvatar = async (userId, file) => {
      setLoading(true);
      setError(null);

      try {
        const formData = new FormData();
        formData.append('avatar', file);

        const response = await axios.post(
          `${API_URL}/users/${userId}/avatar`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        if (response.data?.status === 'success' && response.data.data?.user) {
          setUser(response.data.data.user); // keep context in sync
          return { success: true, user: response.data.data.user };
        }

        const msg = response.data?.message || 'Failed to upload profile image';
        setError(msg);
        return { success: false, error: msg };
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          error.message ||
          'Failed to upload profile image';
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setLoading(false);
      }
    };

    const deleteAvatar = async (userId) => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.delete(`${API_URL}/users/${userId}/avatar`);

        if (response.data?.status === 'success' && response.data.data?.user) {
          setUser(response.data.data.user);
          return { success: true, user: response.data.data.user };
        }

        const msg = response.data?.message || 'Failed to delete profile image';
        setError(msg);
        return { success: false, error: msg };
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          error.message ||
          'Failed to delete profile image';
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setLoading(false);
      }
    };



    return (
        <AuthContext.Provider
          value={{
            user,
            token,
            loading,
            error,
            postLoginLoading,         
            setPostLoginLoading,
            isAuthenticated: !!token && isTokenValid(token),
            register,
            login,
            establishAppSession,
            logout,
            forgotPassword,
            resetPassword,
            updateProfile,
            uploadAvatar,   // ✅ add this
            deleteAvatar,   // ✅ and this
          }}
        >
          {children}
        </AuthContext.Provider>
      );
    };


// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
